import Button from '@material-ui/core/Button';
import FormControl from '@material-ui/core/FormControl';
import FormHelperText from '@material-ui/core/FormHelperText';
import InputLabel from '@material-ui/core/InputLabel';
import MenuItem from '@material-ui/core/MenuItem';
import Select from '@material-ui/core/Select';
import { makeStyles } from '@material-ui/core/styles';
import TextField from '@material-ui/core/TextField';
import Typography from '@material-ui/core/Typography';
import { useFormik } from 'formik';
import { useEffect, useState } from 'react';
import * as Yup from 'yup';

import { Loader, OnboardingWrapper } from '../../shared/components';
import { Clinic } from '../../shared/types';

type ViewProps = {
  isLoading: boolean;
  clinics: Clinic[] | undefined;
  login: () => void;
  requestDynamicLink: (email: string, clinic: Clinic) => void;
};

const useStyles = makeStyles((theme) => ({
  header: {
    fontSize: '19px',
    fontWeight: 600,
    lineHeight: '28.5px',
    letterSpacing: '0.03px',
    marginBottom: '10px',
    color: theme.palette.primary.main,
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
  },
  textFieldsWrapper: {
    padding: '0 15px',
  },
  guidelineParagraph: {
    fontSize: '15px',
    marginBottom: '15px',
    color: theme.palette.text.secondary,
  },
  textField: {
    marginBottom: '30px',
    width: '100%',
    '& label': {
      fontSize: '13px',
    },
    '& input': {
      color: theme.palette.text.secondary,
    },
  },
  button: {
    width: '100%',
    marginBottom: '25px',
    cursor: 'pointer',
    fontSize: '17px',
    '&:disabled': {
      backgroundColor: '#92C6C1',
      color: 'white',
    },
  },
  footer: {
    display: 'flex',
    flexDirection: 'column',
    flexWrap: 'wrap',
    padding: '0 20px',
    lineHeight: '19.5px',
    color: theme.palette.text.secondary,
    '& p': {
      fontSize: '13px',
    },
  },
  haveAccountWrapper: {
    display: 'flex',
    flexWrap: 'wrap',
    marginTop: '10px',
  },
  login: {
    marginLeft: '4px',
    color: theme.palette.primary.main,
    fontWeight: 700,
    cursor: 'pointer',
  },
}));

const validationSchema = Yup.object().shape({
  email: Yup.string().email('Invalid email address').required('Required'),
  clinicName: Yup.string().required('Required'),
});

const View: React.FC<ViewProps> = ({
  isLoading,
  clinics,
  login,
  requestDynamicLink,
}) => {
  const classes = useStyles();
  const initialValues = {
    email: '',
    clinicName: '',
  };
  const [isFormValid, setIsFormValid] = useState<boolean>(false);

  const clinicsList = () => {
    return clinics?.map((clinic) => (
      <MenuItem key={clinic.id} value={clinic.name}>
        {clinic.name}
      </MenuItem>
    ));
  };

  const onSubmit = () => {
    const { email, clinicName } = values;
    if (clinics) {
      const clinic = clinics.find((clinic) => clinic.name === clinicName);
      if (clinic) {
        requestDynamicLink(email, clinic);
      }
    }
  };

  const {
    touched,
    errors,
    values,
    handleSubmit,
    handleChange,
    isValid,
  } = useFormik({
    initialValues,
    validationSchema,
    onSubmit,
  });

  useEffect(() => {
    if (values !== initialValues) {
      setIsFormValid(isValid);
    }
  }, [isValid, values]);

  if (isLoading) {
    return <Loader />;
  }

  return (
    <OnboardingWrapper>
      <>
        <form className={classes.form} onSubmit={handleSubmit}>
          <div className={classes.textFieldsWrapper}>
            <Typography className={classes.header}>Get your link</Typography>
            <Typography className={classes.guidelineParagraph}>
              Enter your email address and choose your clinic and we will send
              you a link to create your account.
            </Typography>
            <TextField
              className={classes.textField}
              id="email"
              name="email"
              label="Email"
              type="email"
              helperText={errors.email}
              error={Boolean(errors.email)}
              variant="outlined"
              onChange={handleChange}
              value={values.email}
            />

            <FormControl variant="outlined" className={classes.textField}>
              <InputLabel id="demo-simple-select-outlined-label">
                Clinic
              </InputLabel>
              <Select
                id="clinicName"
                name="clinicName"
                onChange={handleChange}
                label="Clinic"
                value={values.clinicName}>
                {clinicsList()}
              </Select>
              {errors.clinicName && touched.clinicName && (
                <FormHelperText>{errors.clinicName}</FormHelperText>
              )}
            </FormControl>
          </div>
          <Button
            disabled={!isFormValid}
            color="primary"
            variant="contained"
            type="submit"
            className={classes.button}>
            Send Email
          </Button>
          <div className={classes.footer}>
            <Typography>
              Already have an email from us? Open the link in your welcome email
              on this tablet to log in
            </Typography>
            <div className={classes.haveAccountWrapper}>
              <Typography>Already have an account?</Typography>
              <Typography className={classes.login} onClick={login}>
                Log in
              </Typography>
            </div>
          </div>
        </form>
      </>
    </OnboardingWrapper>
  );
};

export default View;
